import { useEffect, useRef, useState } from "react";
import { heroSlides } from "@/lib/studio";
import { ArrowRight } from "lucide-react";

const PHOTO_DURATION = 4500;
const VIDEO_DURATION = 7000; // give video slides more time to actually read as motion

function HeroSlide({ slide, isActive }) {
  const fgRef = useRef(null);
  const bgRef = useRef(null);
  const isVideo = slide.type === "video";

  useEffect(() => {
    if (!isVideo) return;
    const vids = [fgRef.current, bgRef.current].filter(Boolean);
    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (isActive && !reduceMotion) {
      vids.forEach((v) => {
        v.currentTime = 0;
        v.play().catch(() => { });
      });
    } else {
      vids.forEach((v) => v.pause());
    }
  }, [isActive, isVideo]);

  if (isVideo) {
    return (
      <>
        {/* Ambient blurred fill — desktop only. Smooths the mismatch between
            portrait studio footage and this wide, short banner. */}
        <video
          ref={bgRef}
          src={slide.url}
          poster={slide.poster}
          muted
          loop
          playsInline
          preload="metadata"
          className="hidden md:block absolute inset-0 h-full w-full object-cover scale-110 blur-2xl brightness-[0.45] saturate-75"
        />
        {/* Mobile: full-bleed cover (screen is already portrait, fits naturally).
            Desktop: full uncropped clip, centered and framed over the blurred backdrop. */}
        <video
          ref={fgRef}
          src={slide.url}
          poster={slide.poster}
          muted
          loop
          playsInline
          preload={isActive ? "auto" : "metadata"}
          aria-label={slide.alt}
          className="h-full w-full object-cover md:absolute md:inset-0 md:m-auto md:h-[88%] md:w-auto md:max-w-[88%] md:object-contain md:shadow-2xl md:ring-1 md:ring-cream/10"
        />
      </>
    );
  }

  return (
    <img
      src={slide.url}
      alt={slide.alt}
      className={`h-full w-full object-cover ${isActive ? "hero-slide-active" : ""}`}
      loading="lazy"
    />
  );
}

export default function Hero() {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const current = heroSlides[idx];
    const duration = current.type === "video" ? VIDEO_DURATION : PHOTO_DURATION;
    const t = setTimeout(() => {
      setIdx((prev) => (prev + 1) % heroSlides.length);
    }, duration);
    return () => clearTimeout(t);
  }, [idx]);

  return (
    <section className="relative isolate h-[100svh] min-h-[620px] w-full overflow-hidden" data-testid="hero-section">
      {heroSlides.map((s, i) => (
        <div
          key={s.url}
          className={`absolute inset-0 transition-opacity duration-[1400ms] ${i === idx ? "opacity-100" : "opacity-0"
            }`}
          aria-hidden={i !== idx}
        >
          <HeroSlide slide={s} isActive={i === idx} />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-b from-ink/60 via-ink/40 to-ink/90" />
      <div className="absolute inset-0 grain pointer-events-none" />

      <div className="relative z-10 h-full max-w-7xl mx-auto px-5 md:px-8 flex flex-col justify-end pb-12 md:pb-16">
        <div className="rise max-w-3xl">
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-medium leading-[0.95] tracking-tighter text-cream">
            Ink that tells
            <br />
            <span className="italic font-light text-olive">your</span> story.
          </h1>
          <p className="mt-6 text-cream/70 max-w-xl text-base md:text-lg font-body leading-relaxed">
            A quiet, sterile atelier for custom tattoos and precision piercings. Hand-drawn. Zero templates. Every line intentional.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-5">
            <a
              href="#final-cta"
              className="group inline-flex items-center gap-3 bg-olive hover:bg-olive-hover text-cream px-7 py-4 text-xs tracking-[0.28em] uppercase font-medium transition-all"
              data-testid="hero-book-consultation-btn"
            >
              Book Free Consultation
              <ArrowRight
                size={16}
                className="transition-transform group-hover:translate-x-1"
                strokeWidth={1.6}
              />
            </a>
            <a
              href="#gallery"
              className="inline-flex items-center gap-2 text-cream/80 hover:text-cream text-xs tracking-[0.28em] uppercase transition-colors"
              data-testid="hero-view-work-link"
            >
              View Work
              <span className="h-[1px] w-8 bg-cream/40" />
            </a>
          </div>
        </div>

        {/* Slide indicators */}
        <div className="absolute bottom-10 right-5 md:right-8 flex items-center gap-2">
          {heroSlides.map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              className={`h-[2px] transition-all ${i === idx ? "w-10 bg-olive" : "w-5 bg-cream/30 hover:bg-cream/60"
                }`}
              aria-label={`Go to slide ${i + 1}`}
              data-testid={`hero-slide-indicator-${i}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}